import { FC, ReactNode } from 'react'
import { v4 as uuid } from 'uuid'
import { Box, Interpolation, SxProps, Theme, useTheme } from '@mui/material'

import { backgroundTableByMode, borderTableByMode } from '@helpers'
import {
  DATAGRID_HEADERS_SHADOW,
  DATAGRID_PINNED_SEPARATOR_WIDTH,
  DATAGRID_ROW_WINDOW_HEIGHT,
  DATAGRID_SCROLL_WIDTH,
  DEFAULT_HEADER_STYLES_CLASSNAME,
  ENTITY,
  FLEXIBLE,
} from '@constants'
import { ObjectDataRecord } from '@types'

type RowStylesWrapperProps = {
  currentPage?: number
  objectData: ObjectDataRecord[]
  pageSize: number
  children: ReactNode
  type?: ENTITY
  globalStyles?: Interpolation<Theme>
  isDialogWindow?: boolean
  hasScrollbar: boolean
  hasWrapRowText?: boolean
  hasWrapHeaderText?: boolean
  isEnabledLocalParameters?: boolean
  headerHeight?: number | null
  headerGroupHeight?: number | null
}
const wrapperClassName = `configured-view-wrapper-${uuid()}`

export const RowStylesWrapper: FC<RowStylesWrapperProps> = ({
  pageSize,
  objectData,
  children,
  isEnabledLocalParameters = false,
  headerHeight,
  headerGroupHeight,
  hasScrollbar,
  hasWrapRowText,
  hasWrapHeaderText,
}) => {
  // TODO: Найти лучший способ отслеживания padding-right.
  // На январь 2023, это проблема самой библиотеки:
  // https://material-ui-docs.netlify.app/x/react-data-grid/#commercial-version
  // (для воспроизведения бага в одном из примеров нужно вручную закрепить колонку)
  // const [rightPadding, setRightPadding] = useState<number>(getScrollbarWidth())
  const theme = useTheme()

  // useEffect(() => {
  //   if (!objectData || !pageSize) {
  //     return
  //   }

  //   if (objectData.length === 0) {
  //     return
  //   }

  //   if (objectData?.length > pageSize) {
  //     setRightPadding(getScrollbarWidth())
  //     return
  //   }

  //   if (currentPage > 1 || objectData?.length < pageSize) {
  //     setRightPadding(0)
  //   }
  // }, [objectData, pageSize])

  const stylesForWrapHeaderText: SxProps<Theme> = {
    ['& .MuiDataGrid-menuIcon']: {
      zIndex: 2,
    },
    ['& .MuiDataGrid-iconButtonContainer']: {
      zIndex: 2,
    },
    ['& .MuiDataGrid-columnHeaderDraggableContainer']: {
      position: 'relative',
      overflow: 'hidden',
    },
    ['.MuiDataGrid-columnHeader:not(.action-column-header)']: {
      overflow: 'hidden',
      '&:hover': {
        '&::before': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '30px',
          zIndex: 1,
          height: '100%',
          background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 10%)',
        },
      },
    },
    ['& .MuiDataGrid-columnHeaderTitle']: {
      fontWeight: 'inherit',
      wordBreak: 'break-all',
      whiteSpace: 'pre-wrap',
    },
    ['& .MuiDataGrid-columnHeaderTitleContainerContent']: {
      wordBreak: 'break-all',
      whiteSpace: 'pre-wrap',
    },
  }

  const stylesForCustomHeaders: SxProps<Theme> = {
    ' & .MuiDataGrid-columnHeaders': {
      minHeight: `${
        headerHeight && headerGroupHeight ? headerHeight + headerGroupHeight : headerHeight
      }px !important`,
      maxHeight: `${headerHeight}px !important`,
      boxShadow: DATAGRID_HEADERS_SHADOW,
      top: '-4px',
    },
    [`& .${DEFAULT_HEADER_STYLES_CLASSNAME}`]: {
      minHeight: `${headerHeight}px !important`,
      maxHeight: `${headerHeight}px !important`,
    },
  }

  const getPinnedHeaderSeparatorStyles = (side: 'left' | 'right') => ({
    [`border${side === 'left' ? 'Left' : 'Right'}`]: 'none !important',
    boxShadow: `${side === 'left' ? '-' : ''}${DATAGRID_PINNED_SEPARATOR_WIDTH}px 0 0 0 ${theme.palette.background.separator}`,
  })

  return (
    <Box
      sx={{
        ...FLEXIBLE,
        '& .MuiDataGrid-row': {
          color: `${theme.palette.color} !important`,
          transition: 'background-color 0.3s linear',
          minHeight: '24px',
          ...(hasWrapRowText &&
            isEnabledLocalParameters && {
              '& .MuiDataGrid-cell': {
                wordBreak: 'break-all',
                whiteSpace: 'normal !important',
                display: 'flex',
                alignItems: 'center',
                maxHeight: 'none !important',
              },
            }),
        },
        ['.MuiDataGrid-cell']: {
          whiteSpace: 'nowrap !important',
          maxHeight: `${DATAGRID_ROW_WINDOW_HEIGHT}px !important`,
          minHeight: '24px',
        },
        [`.MuiDataGrid-pinnedColumnHeaders--right`]: {
          // TODO pinnedColumnHeaders Возможно удалён
          paddingRight:
            objectData?.length > pageSize
              ? `${hasScrollbar ? DATAGRID_SCROLL_WIDTH : 0}px !important`
              : `0px !important`,
        },
        '& .MuiDataGrid-pinnedColumns': {
          bgcolor: `${theme.palette.background.pinned} !important`,
          right: '-1px',
        },
        '.MuiDataGrid-cell--withRightBorder': {
          boxShadow: `${DATAGRID_PINNED_SEPARATOR_WIDTH}px 0 0 0 ${theme.palette.background.separator}`,
          borderRight: 'none !important',
        },
        '.MuiDataGrid-columnHeader--withRightBorder:not(.MuiDataGrid-columnHeader--filledGroup):not(.MuiDataGrid-columnHeader--emptyGroup)':
          getPinnedHeaderSeparatorStyles('right'),
 
        '.MuiDataGrid-columnHeader--withLeftBorder:not(.MuiDataGrid-columnHeader--filledGroup):not(.MuiDataGrid-columnHeader--emptyGroup)':
          getPinnedHeaderSeparatorStyles('left'),
        '.MuiDataGrid-columnHeader--lastUnpinned':
          {
            transform: `translateX(-${DATAGRID_PINNED_SEPARATOR_WIDTH}px)`,
          },
        '.MuiDataGrid-cell--withLeftBorder': {
          boxShadow: `-${DATAGRID_PINNED_SEPARATOR_WIDTH}px 0 0 0 ${theme.palette.background.separator}`,
          borderLeft: 'none !important',
        },
        '& .MuiDataGrid-filler--pinnedLeft': {
          borderRight: 'none !important',
        },
        '& .MuiDataGrid-filler--pinnedRight': {
          borderLeft: 'none !important',
        },
        [`.${wrapperClassName} .MuiDataGrid-pinnedColumnHeaders--right`]: {
          // pinnedColumnHeaders TODO Возможно удалён
          paddingRight: `${hasScrollbar ? DATAGRID_SCROLL_WIDTH : 0}px !important`,
        },
        ['& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-iconButtonContainer']: {
          width: 40,
        },
        ['& .MuiDataGrid-columnHeader .MuiBadge-badge']: {
          color: theme => `${theme.palette.color.default}`,
          fontSize: '0.6rem',
          top: 12,
          right: 4,
        },
        ['& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon']: {
          fill: theme => `${theme.palette.color.default}`,
          fontSize: '1rem',
        },
        ['& .MuiDataGrid-columnHeaderTitle']: {
          pr: 1,
          fontWeight: 'inherit',
        },
        ['& .MuiDataGrid-columnSeparator']: {
          height: '100%',
        },
        ['& .MuiDataGrid-iconSeparator']: {
          transform: 'scaleY(20)',
        },
        [`.${wrapperClassName} .MuiDataGrid-virtualScroller::-webkit-scrollbar`]: {
          height: '10px',
          width: `${hasScrollbar ? DATAGRID_SCROLL_WIDTH : 0}px`,
          background: theme.palette.background.hovered,
        },
        [`.${wrapperClassName} > .MuiGrid-root`]: {
          mb: 0,
        },
        [`.${wrapperClassName} .MuiDataGrid-virtualScroller::-webkit-scrollbar-track`]: {
          background: theme.palette.background.hovered,
        },
        [`.${wrapperClassName} .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb`]: {
          background: theme.palette.color.scroll,
          border: theme.palette.border.scroll,
        },
        '& .MuiDataGrid-pinnedColumns--left, .MuiDataGrid-pinnedColumnHeaders--left': {
          // TODO pinnedColumnHeaders Возможно удалён
          zIndex: 2,
        },
        ...(hasWrapHeaderText && isEnabledLocalParameters && stylesForWrapHeaderText),
        ...stylesForCustomHeaders,
        ...backgroundTableByMode(wrapperClassName, theme),
        ...borderTableByMode(wrapperClassName, theme),
      }}
    >
      <Box className={wrapperClassName} sx={FLEXIBLE}>
        {children}
      </Box>
    </Box>
  )
}
